import { create } from 'zustand'
import { dedupeByPath, sortForGallery } from '@/core/session'
import type { ImageFile } from '@/core/types'
import {
  MONITORED_HANDLE,
  TARGET_HANDLE,
  ensurePermission,
  loadHandle,
  pickDirectory,
  saveHandle,
} from '@/app/lib/local/fs'
import {
  isWatching,
  knownFiles,
  poll,
  resetWatch,
  startWatch,
  stopWatch,
  type WatchEvent,
} from '@/app/lib/local/watcher'
import { releaseThumbs } from '@/app/lib/local/thumbs'
import { loadSettings, saveSettings, type Settings } from '@/app/lib/local/settings'
import type { QueueEvent } from '@/app/lib/local/queue'
import { probe, type Capabilities } from '@/app/lib/capabilities'
import { listLayouts } from '@/app/lib/local/layouts'

/**
 * Machine-level state: the operator's settings, the two folder handles, and the
 * photos the watcher has seen.
 *
 * The folder handles are state rather than settings because they cannot be
 * serialised into one — a `FileSystemDirectoryHandle` is the only thing that can
 * open a folder now that there are no paths, and its permission can lapse
 * between one launch and the next. `settings` carries only the display names,
 * so a screen can say which folder is chosen without holding the handle.
 *
 * Anything belonging to one guest's trip through the console lives in
 * `useFlowStore` instead, so finishing a session cannot leave stale selection
 * behind for the next one.
 */
export interface UploadProgress {
  sessionKey: string
  done: number
  total: number
  error?: string
}

export interface AppState {
  settings: Settings | null
  /**
   * What this browser can currently do. Null until the first probe returns.
   *
   * It lives here rather than in AppShell because it is no longer only that
   * shell's business: with the setup screen gone, the flow's own controls are
   * what enforce it, and a screen should not have to be handed the answer
   * through three components to know whether it may run.
   */
  capabilities: Capabilities | null

  monitored: FileSystemDirectoryHandle | null
  target: FileSystemDirectoryHandle | null

  watching: boolean
  /** Everything the watcher has seen, newest first. */
  files: ImageFile[]
  /** Seen since the last gallery load — drives the auto-refresh. */
  pendingCount: number

  /** Sessions currently going up, newest last. Cleared as each completes. */
  uploads: UploadProgress[]

  watchError: string | null

  load: () => Promise<void>
  /** Re-probes the prerequisites. Cheap; safe to call on every navigation. */
  checkCapabilities: () => Promise<Capabilities>
  updateSettings: (patch: Partial<Settings>) => Promise<void>
  chooseMonitored: () => Promise<boolean>
  chooseTarget: () => Promise<boolean>
  regrant: () => Promise<boolean>

  toggleWatch: () => Promise<void>
  refreshGallery: () => Promise<void>

  applyWatchEvent: (event: WatchEvent) => void
  applyQueueEvent: (event: QueueEvent) => void
}

export const useAppStore = create<AppState>((set, get) => ({
  settings: null,
  capabilities: null,

  monitored: null,
  target: null,

  watching: false,
  files: [],
  pendingCount: 0,
  uploads: [],
  watchError: null,

  load: async () => {
    const settings = await loadSettings()
    const monitored = await loadHandle(MONITORED_HANDLE)
    const target = await loadHandle(TARGET_HANDLE)

    set({
      settings,
      monitored,
      target,
      watching: isWatching(),
      files: sortForGallery(dedupeByPath(knownFiles())),
    })
  },

  checkCapabilities: async () => {
    const layouts = await listLayouts().catch(() => [])
    const capabilities = await probe(get().settings, layouts.length)
    set({ capabilities })
    return capabilities
  },

  updateSettings: async (patch) => {
    const settings = await saveSettings(patch)
    set({ settings })
  },

  chooseMonitored: async () => {
    const handle = await pickDirectory('citaria-monitored', 'read')
    if (!handle) return false

    await saveHandle(MONITORED_HANDLE, handle)
    // A new folder means the old folder's photos are no longer openable. The
    // agent forgot them on start for exactly this reason: selecting a file it
    // could not serve failed later, at composite time.
    resetWatch()
    releaseThumbs()

    set({ monitored: handle, files: [], pendingCount: 0, watching: false })
    await get().updateSettings({ monitoredFolderName: handle.name })
    await get().checkCapabilities()
    return true
  },

  chooseTarget: async () => {
    const handle = await pickDirectory('citaria-target', 'readwrite')
    if (!handle) return false

    await saveHandle(TARGET_HANDLE, handle)
    set({ target: handle })
    await get().updateSettings({ targetFolderName: handle.name })
    await get().checkCapabilities()
    return true
  },

  /**
   * Re-grants both stored handles.
   *
   * Chrome downgrades a stored handle to "prompt" across a browser restart and
   * only re-grants it from a user gesture, so this is wired to buttons in
   * Settings and never called from an effect. The setup screen that used to own
   * that button is gone; Settings is where an evening starts now.
   */
  regrant: async () => {
    const { monitored, target } = get()
    if (!monitored) return false

    const granted = await ensurePermission(monitored, 'read')
    // Asked for in the same gesture, but reported separately: both folders are
    // required, and a refusal here must not be read as the camera folder
    // failing. checkCapabilities below is what actually settles each one.
    if (target) await ensurePermission(target, 'readwrite').catch(() => false)

    await get().checkCapabilities()
    return granted
  },

  toggleWatch: async () => {
    const { watching, monitored } = get()
    if (watching) {
      stopWatch()
      set({ watching: false })
      return
    }

    if (!monitored) throw new Error('Folder kamera belum dipilih')
    if (!(await ensurePermission(monitored, 'read'))) {
      throw new Error('Izin folder kamera ditolak')
    }

    await startWatch(monitored)
    set({ watching: true, watchError: null })
    await get().refreshGallery()
  },

  refreshGallery: async () => {
    const { monitored } = get()
    if (!monitored) return
    await poll()
    set({ files: sortForGallery(dedupeByPath(knownFiles())), pendingCount: 0 })
  },

  applyWatchEvent: (event) => {
    switch (event.type) {
      case 'file.new':
        // The watcher only announces a file once its size has settled, so it is
        // safe to pull straight into the grid.
        set((state) => ({ pendingCount: state.pendingCount + 1 }))
        break
      case 'watch.state':
        set({ watching: event.running })
        break
      case 'watch.error':
        set({ watchError: event.error })
        break
    }
  },

  applyQueueEvent: (event) => {
    set((state) => {
      const others = state.uploads.filter((u) => u.sessionKey !== event.sessionKey)
      switch (event.type) {
        case 'upload.progress':
          return {
            uploads: [...others, { sessionKey: event.sessionKey, done: event.done, total: event.total }],
          }
        case 'upload.failed': {
          const current = state.uploads.find((u) => u.sessionKey === event.sessionKey)
          return {
            uploads: [
              ...others,
              {
                sessionKey: event.sessionKey,
                done: current?.done ?? 0,
                total: current?.total ?? 0,
                error: event.error,
              },
            ],
          }
        }
        case 'upload.done':
          return { uploads: others }
      }
    })
  },
}))

/**
 * Resolves selected identifiers back to files, preserving the order they were
 * chosen in — that order is what pairs each photo with a layout slot.
 */
export function resolveSelected(files: ImageFile[], selected: string[]): ImageFile[] {
  const byId = new Map(files.map((f) => [f.identifier, f]))
  return selected.flatMap((id) => {
    const file = byId.get(id)
    return file ? [file] : []
  })
}
